import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, incluyendo tu nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información personal se utiliza exclusivamente para: procesar tu pedido, enviarte el acceso al producto digital, y comunicarte actualizaciones importantes relacionadas con tu compra. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines comerciales.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales indicados en tu correo de confirmación de compra.'
  }, {
    heading: 'Cambios en esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al realizar tu compra en Despertar Sin Dolor, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te recomendamos no realizar la compra.'
  }, {
    heading: 'Producto Digital',
    text: 'Despertar Sin Dolor es un producto de información digital que incluye guías, protocolos y material educativo. El acceso se entrega de forma inmediata tras confirmar el pago. Este producto es para uso personal y no puede ser redistribuido, copiado ni revendido.'
  }, {
    heading: 'Descargo de Responsabilidad Médica',
    text: 'El contenido de este producto es únicamente informativo y educativo. No sustituye el consejo, diagnóstico o tratamiento médico profesional. Consulta siempre con tu médico antes de iniciar cualquier programa de ejercicios o cambiar tu rutina de salud, especialmente si tienes condiciones médicas preexistentes.'
  }, {
    heading: 'Resultados',
    text: 'Los resultados pueden variar de persona a persona. Los testimonios y ejemplos mencionados representan experiencias individuales y no garantizan que obtendrás los mismos resultados. Tu compromiso y constancia son factores determinantes.'
  }, {
    heading: 'Pago y Acceso',
    text: 'El precio del producto es un pago único. Una vez procesado el pago, recibirás acceso inmediato al contenido digital. No se realizan cobros recurrentes ni suscripciones.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido del producto, incluyendo textos, imágenes, diseños y metodologías, está protegido por derechos de autor. Queda prohibida su reproducción total o parcial sin autorización expresa por escrito.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'En la máxima medida permitida por la ley, Despertar Sin Dolor no será responsable por daños directos, indirectos, incidentales o consecuentes derivados del uso o la imposibilidad de uso del producto.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-26">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-stone-900 border border-stone-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-27">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-800 sticky top-0 bg-stone-900 z-10" data-id="element-28">
          <h2 className="text-2xl font-black text-stone-50" data-id="element-29">{content.title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-stone-800 transition-colors text-stone-400 hover:text-stone-200" data-id="element-30">
            <X className="w-6 h-6" data-id="element-31" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-32">
          {content.sections.map((section, index) => <div key={index} data-id="element-33">
              <h3 className="text-lg font-bold text-amber-500 mb-2" data-id="element-34">
                {section.heading}
              </h3>
              <p className="text-stone-400 leading-relaxed" data-id="element-35">{section.text}</p>
            </div>)}

          <p className="text-stone-600 text-sm pt-4 border-t border-stone-800" data-id="element-36">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-stone-950 text-stone-500 py-12 px-4 border-t border-stone-900 text-center" data-id="element-37">
        <div className="max-w-4xl mx-auto" data-id="element-38">
          <p className="text-sm mb-6" data-id="element-39">
            Este producto no sustituye el consejo médico profesional. Consulte a
            su médico antes de comenzar cualquier programa de ejercicios.
          </p>

          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-40">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-stone-500 hover:text-amber-500 transition-colors underline underline-offset-4" data-id="element-41">
              Política de Privacidad
            </button>
            <span className="text-stone-700" data-id="element-42">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-stone-500 hover:text-amber-500 transition-colors underline underline-offset-4" data-id="element-43">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-sm" data-id="element-44">
            &copy; {new Date().getFullYear()} Despertar Sin Dolor. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-45">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-46" />}
      </AnimatePresence>
    </>;
}